import {
  List,
  Datagrid,
  TextField,
  NumberField,
  TextInput,
  SelectInput,
  Show,
  SimpleShowLayout,
  FunctionField,
  RaRecord,
} from 'react-admin';
import { Stack } from '@mui/material';
import { useCompanies, companyChoices, companyNameByViaId } from '../shared/companies';
import { MessageContentPanel } from './MessageContentPanel';
import { MessageUploadPanel } from './MessageUploadPanel';
import { ClampedText, CompanyChip, compactDatagridSx } from '../shared/listCells';
import { VIA_MESSAGE_TYPES, viaMessageTypeLabel } from '../shared/status';

/**
 * Filtr rodzaju komunikatu: wartość to kod z bazy (`assigned`…) — react-admin
 * zapamiętuje ją w filtrze — a nazwa słowem (07.10.2026, wcześniej kod).
 */
export const messageTypeChoices = VIA_MESSAGE_TYPES.map((id) => ({
  id,
  name: viaMessageTypeLabel(id),
}));

/**
 * Filtr spółki bierze listę Z DANYCH.
 *
 * Wcześniej numery spółek były wpisane w tym pliku — patrz `shared/companies.ts`,
 * dlaczego to problem, a nie drobiazg.
 */
const CompanyFilter = (props: Record<string, unknown>) => {
  const companies = useCompanies();
  return (
    <SelectInput
      {...props}
      source="viaCompanyId"
      label="Spółka"
      choices={companyChoices(companies)}
    />
  );
};

const filters = [
  <TextInput source="q" label="Szukaj" alwaysOn />,
  <CompanyFilter source="viaCompanyId" />,
  <SelectInput source="messageType" label="Typ komunikatu" choices={messageTypeChoices} />,
];

/** Nazwa spółki z danych; nieznany numer pokazujemy, jak przyszedł. */
const Company = ({ record }: { record: RaRecord }) => {
  const companies = useCompanies();
  return (
    <span>
      {companyNameByViaId(companies, (record as { viaCompanyId?: string }).viaCompanyId) || '—'}
    </span>
  );
};

/**
 * Spółka na liście — znacznik z krajem, pełna nazwa w podpowiedzi.
 * Nieznany numer idzie w znacznik, jak przyszedł (nie zgadujemy kraju).
 */
const CompanyTag = ({ record }: { record: RaRecord }) => {
  const companies = useCompanies();
  const viaCompanyId = (record as { viaCompanyId?: string }).viaCompanyId;
  const known = companies.find((s) => s.viaCompanyId === viaCompanyId);
  return (
    <CompanyChip
      name={companyNameByViaId(companies, viaCompanyId)}
      country={known?.country}
    />
  );
};


// Robocza lista zleceń z parsera VIA (task 869ee11y1).
export const ViaOrderList = () => (
  <List
    filters={filters}
    sort={{ field: 'updatedAt', order: 'DESC' }}
    perPage={25}
  >
    {/*
      Wczytanie komunikatu to jedyna droga, którą dane z VIA trafiają dziś
      do systemu — stąd nad listą, a nie schowane w szczegółach. Nad tabelą,
      jak pasek synchronizacji na innych listach: jako `aside` (boczna
      kolumna) ściskał tabelę do dwóch trzecich szerokości (25.09.2026).
    */}
    <Stack spacing={1}>
      <MessageUploadPanel />
      {/* Wiersz w jednej linii od 1366 px (28.09.2026): długie nazwy klientów
          i miast skracane z podpowiedzią, spółka jako znacznik z krajem. */}
      <Datagrid rowClick="show" bulkActionButtons={false} sx={compactDatagridSx}>
        <TextField source="orderNumber" label="Nr zlecenia" sx={{ whiteSpace: 'nowrap' }} />
        <FunctionField
          source="messageType"
          label="Typ"
          render={(r: RaRecord) => viaMessageTypeLabel(r.messageType as string)}
        />
        <FunctionField
          label="Spółka"
          render={(r: RaRecord) => <CompanyTag record={r} />}
        />
        <FunctionField
          source="clientName"
          label="Klient"
          render={(r: RaRecord) => <ClampedText text={r.clientName} maxWidth={{ xs: 200, xl: 320 }} />}
        />
        <FunctionField
          source="startCity"
          label="Załadunek"
          render={(r: RaRecord) => <ClampedText text={r.startCity} maxWidth={{ xs: 140, xl: 220 }} />}
        />
        <FunctionField
          source="endCity"
          label="Rozładunek"
          render={(r: RaRecord) => <ClampedText text={r.endCity} maxWidth={{ xs: 140, xl: 220 }} />}
        />
        <TextField source="startDate" label="Data zał." sx={{ whiteSpace: 'nowrap' }} />
        <NumberField source="price" label="Cena" sx={{ whiteSpace: 'nowrap' }} />
        <TextField source="currency" label="Waluta" />
      </Datagrid>
    </Stack>
  </List>
);

export const ViaOrderShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="transportId" label="ID transportu (tour)" />
      <TextField source="orderNumber" label="Nr zlecenia" />
      <FunctionField
        label="Typ komunikatu"
        render={(r: RaRecord) => viaMessageTypeLabel(r.messageType as string)}
      />
      {/* Bez „Kwalifikatora” (07.10.2026): techniczne pole formatu tisys,
          operatorowi nic nie mówi — zostaje w surowym poddrzewie niżej. */}
      <FunctionField label="Spółka" render={(r: RaRecord) => <Company record={r} />} />
      <TextField source="clientName" label="Klient" />
      <TextField source="disponentName" label="Dyspozytor" />
      <TextField source="startCity" label="Miasto załadunku" />
      <TextField source="endCity" label="Miasto rozładunku" />
      <NumberField source="price" label="Cena" />
      <TextField source="currency" label="Waluta" />
      <MessageContentPanel />
      {/*
        Surowe poddrzewo zostaje jako furtka: format klienta jest otwarty,
        więc gdy w komunikacie będzie coś, czego jeszcze nie nazwaliśmy,
        musi być gdzie to zobaczyć. Domyślnie zwinięte — przez pół roku było
        pierwszym, co widział operator, i przesłaniało treść zlecenia.
      */}
      <FunctionField
        label="Pełne dane (raw)"
        render={(r: RaRecord) => (
          <details>
            <summary style={{ cursor: 'pointer' }}>
              Surowe poddrzewo komunikatu
            </summary>
            <pre style={{ maxHeight: 420, overflow: 'auto', fontSize: 12 }}>
              {JSON.stringify(r.raw, null, 2)}
            </pre>
          </details>
        )}
      />
    </SimpleShowLayout>
  </Show>
);
